<template>
  <div class="sdw-banner">
    <!-- Header -->
    <div class="sdw-header">
      <span class="sdw-icon">⚠</span>
      <div class="sdw-header-body">
        <span class="sdw-title">Diff may be out of date</span>
        <span class="sdw-subtitle">
          {{ affectedWoCount }} work order{{
            affectedWoCount !== 1 ? "s" : ""
          }}
          {{ affectedWoCount === 1 ? "has" : "have" }} changed since the diff
          was computed. Proceeding with this diff may apply outdated changes.
        </span>
      </div>
    </div>

    <!-- Detail -->
    <!-- Not required for now, but leaving in case we want to add a detail message in the future -->
    <!-- <div v-if="detail" class="sdw-detail">
      {{ detail }}
    </div> -->

    <!-- Stale work orders -->
    <div v-if="staleWos.length" class="sdw-section">
      <div class="sdw-section-title">
        {{ staleWos.length }} work order{{ staleWos.length !== 1 ? "s" : "" }}
        modified since diff was computed
      </div>
      <div
        v-for="wo in staleWos"
        :key="wo.Wono"
        :class="[
          'sdw-stale-item',
          { 'sdw-stale-item-open': expandedWos[wo.Wono] },
        ]"
      >
        <!-- Collapsible header -->
        <div class="sdw-stale-hdr" @click="toggleWo(wo.Wono)">
          <span class="sdw-chevron">{{
            expandedWos[wo.Wono] ? "▼" : "▶"
          }}</span>
          <span class="sdw-wono">WO {{ wo.Wono }}</span>
          <template v-if="wo.WoDescription">
            <span class="sdw-sep">—</span>
            <span class="sdw-wo-desc">{{ wo.WoDescription }}</span>
          </template>
          <span v-if="wo.WoStatus" class="sdw-wo-status">{{
            statusLabel(wo.WoStatus)
          }}</span>
          <span class="sdw-lines-count">
            {{ (wo.ChangedLines || []).length }} line{{
              (wo.ChangedLines || []).length !== 1 ? "s" : ""
            }}
            changed
          </span>
        </div>

        <!-- Changed lines table -->
        <div v-if="expandedWos[wo.Wono]" class="sdw-lines-table">
          <div class="sdw-lines-head">
            <div class="sdw-lth sdw-lcol-proc">Process</div>
            <div class="sdw-lth sdw-lcol-code">Material</div>
            <div class="sdw-lth sdw-lcol-val">Stored qty</div>
            <div class="sdw-lth sdw-lcol-val">Current qty</div>
            <div class="sdw-lth sdw-lcol-chg">Change</div>
          </div>
          <div v-for="(line, i) in wo.ChangedLines" :key="i" class="sdw-ltr">
            <div class="sdw-ltc sdw-lcol-proc">
              {{ line.ProcessCode || "—" }}
            </div>
            <div class="sdw-ltc sdw-lcol-code sdw-code-bold">
              {{ line.MaterialCode }}
            </div>
            <div class="sdw-ltc sdw-lcol-val sdw-val-old">
              {{ formatVal(line.StoredUnitQty) }}
            </div>
            <div class="sdw-ltc sdw-lcol-val sdw-val-new">
              {{ formatVal(line.CurrentUnitQty) }}
            </div>
            <div class="sdw-ltc sdw-lcol-chg">
              <span class="sdw-pill sdw-pill-amber">{{
                line.ChangeType || "Changed"
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action section -->
    <div v-if="!isReadOnly" class="sdw-actions">
      <div class="sdw-action-col">
        <span class="sdw-action-label">Option 1 — re-run diff</span>
        <p class="sdw-action-desc">
          Re-runs the diff against the current work order data. Recommended if
          you want the most up-to-date preview before applying.
        </p>
        <DxButton
          text="Re-run diff"
          type="default"
          styling-mode="contained"
          class="sdw-recompute-btn"
          @click="$emit('recompute')"
        />
      </div>

      <div class="sdw-action-divider">or</div>

      <div class="sdw-action-col">
        <span class="sdw-action-label">Option 2 — acknowledge and proceed</span>
        <p class="sdw-action-desc">
          Optionally note why proceeding with the stored diff is acceptable. The
          note will be recorded in the activity log.
        </p>
        <textarea
          v-model.trim="acknowledgementNote"
          class="sdw-note-input"
          placeholder="Optional note..."
          rows="2"
          maxlength="500"
        />
        <span class="sdw-proceed-wrap">
          <DxButton
            text="Acknowledge and proceed"
            type="normal"
            styling-mode="outlined"
            class="sdw-proceed-btn"
            @click="onProceed"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcWoBulkUpdateStaleDiffBanner",
  components: { DxButton },
  props: {
    affectedWoCount: { type: Number, default: 0 },
    detail: { type: String, default: null },
    staleWos: {
      type: Array,
      default: function () {
        return [];
      },
    },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      acknowledgementNote: "",
      expandedWos: {},
    };
  },
  created() {
    // Default all stale WOs to expanded so changed lines are visible immediately
    this.staleWos.forEach((wo) => {
      this.$set(this.expandedWos, wo.Wono, true);
    });
  },
  methods: {
    toggleWo(wono) {
      this.$set(this.expandedWos, wono, !this.expandedWos[wono]);
    },
    onProceed() {
      this.$emit("acknowledge", this.acknowledgementNote);
    },
    statusLabel(status) {
      const map = {
        D: "Draft",
        P: "Planning",
        A: "Awaiting Production",
        I: "In Production",
        R: "Review",
        C: "Complete",
        X: "Cancelled",
      };
      return map[(status || "").trim()] || status;
    },
    formatVal(value) {
      if (value == null) return "—";
      if (typeof value === "string") return value;
      return formatNumber(value, "##,###,###,###,##0.####");
    },
  },
};
</script>

<style lang="scss">
.sdw-banner {
  border: 1px solid #f79009;
  border-radius: 8px;
  background: #fffcf0;
  margin-bottom: 10px;
  overflow: hidden;
}

// ── Header ────────────────────────────────────────────────────────────────────
.sdw-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fff3c7;
  border-bottom: 1px solid #f79009;
}

.sdw-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #b54708;
}

.sdw-header-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sdw-title {
  font-size: 14px;
  font-weight: 700;
  color: #7a2e0e;
}

.sdw-subtitle {
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
}

// ── Detail ────────────────────────────────────────────────────────────────────
.sdw-detail {
  padding: 10px 16px;
  border-bottom: 1px solid #fde8a0;
  font-size: 13px;
  color: #7a2e0e;
  line-height: 1.4;
}

// ── Stale WO section ──────────────────────────────────────────────────────────
.sdw-section {
  padding: 12px 16px;
  border-bottom: 1px solid #fde8a0;
}

.sdw-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #92400e;
  margin-bottom: 10px;
}

.sdw-stale-item {
  border: 1px solid #fde8a0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.sdw-stale-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fffbeb;
  cursor: pointer;
  user-select: none;
  flex-wrap: wrap;

  &:hover {
    background: #fef3c7;
  }
}

.sdw-chevron {
  font-size: 9px;
  color: #92400e;
  flex-shrink: 0;
}

.sdw-wono {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.sdw-sep {
  color: #adb5bd;
  font-size: 13px;
}

.sdw-wo-desc {
  font-size: 13px;
  color: #344054;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sdw-wo-status {
  font-size: 11px;
  font-weight: 600;
  color: #b54708;
  white-space: nowrap;
}

.sdw-lines-count {
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #fde8a0;
  padding: 1px 8px;
  border-radius: 9999px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

// ── Changed lines table ───────────────────────────────────────────────────────
.sdw-lines-table {
  font-size: 12px;
  border-top: 1px solid #fde8a0;
}

.sdw-lines-head {
  display: flex;
  background: #fef9e7;
  border-bottom: 1px solid #fde8a0;
}

.sdw-lth {
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.sdw-ltr {
  display: flex;
  border-bottom: 1px solid #fef3c7;

  &:last-child {
    border-bottom: none;
  }
}

.sdw-ltc {
  padding: 5px 8px;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sdw-lcol-proc {
  width: 90px;
  flex-shrink: 0;
}

.sdw-lcol-code {
  width: 110px;
  flex-shrink: 0;
}

.sdw-lcol-val {
  width: 120px;
  flex-shrink: 0;
  text-align: right;
}

.sdw-lcol-chg {
  width: 120px;
  flex-shrink: 0;
}

.sdw-code-bold {
  font-weight: 600;
}

.sdw-val-old {
  color: #6c757d;
  text-decoration: line-through;
}

.sdw-val-new {
  font-weight: 600;
  color: #101828;
}

.sdw-pill {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;

  &.sdw-pill-amber {
    background: #fef0c7;
    color: #92400e;
    border: 1px solid #f79009;
  }
}

// ── Actions ───────────────────────────────────────────────────────────────────
.sdw-actions {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 16px;
}

.sdw-action-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sdw-action-label {
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sdw-action-desc {
  font-size: 12px;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

.sdw-action-divider {
  flex-shrink: 0;
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #adb5bd;
  padding-top: 48px;
}

.sdw-note-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  min-height: 52px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #03a9f4;
    box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.15);
  }

  &::placeholder {
    color: #adb5bd;
  }
}

.sdw-recompute-btn {
  align-self: flex-start;
}

.sdw-proceed-wrap {
  align-self: flex-start;
}
</style>
